import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

export type ToastType = "success" | "error";

interface ToastProps {
  message: string;
  type: ToastType;
  onClose?: () => void;
}

const Toast = ({ message, type, onClose }: ToastProps) => {
  const [ isVisible, setIsVisible ] = useState(true);
  const [ isExiting, setIsExiting ] = useState(false);

  const handleClose = useCallback((ms: number = 3500) => {
    setTimeout(() => {
      setIsExiting(true);
      // gives it an animation for 300ms
      setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, 300);
    }, ms);
  }, [ onClose ]);

  useEffect(() => {
    if (isVisible && !isExiting) {
      handleClose();
    }
  }, [ isVisible, isExiting, handleClose ]);

  const handleManualClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  }, [ onClose ]);

  if (!isVisible) return null;

  const isSuccess = type === "success";

  const toastBackground = "bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200/50 text-emerald-900";
  const toastBackgroundError = "bg-gradient-to-r from-red-50 to-rose-50 border-red-200/50 text-red-900";
  return (
    <div
      className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[1000]
        ${isExiting ? "translate-y-[-100%] opacity-0" : "translate-y-0 opacity-100"}
        transition-all duration-300 ease-out z-[9999]
      `}
      role="alert"
      aria-live="assertive"
    >
      <div
        className={twMerge(`flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl backdrop-blur-sm border
          min-w-[320px] max-w-[500px] ${isSuccess ? toastBackground : toastBackgroundError}`)}
      >
        <div
          className={`
            flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
            ${isSuccess
              ? "bg-emerald-100 text-emerald-600"
              : "bg-red-100 text-red-600"
            }
          `}
        >
          {isSuccess ? (
            <CheckCircleIcon className="w-6 h-6" />
          ) : (
            <XCircleIcon className="w-6 h-6" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-5">{message}</p>
        </div>

        {onClose && (
          <button
            className={`
              flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center
              transition-all duration-200
              ${isSuccess
                ? "hover:bg-emerald-100 text-emerald-600 hover:text-emerald-700"
                : "hover:bg-red-100 text-red-600 hover:text-red-700"
              }
              focus:outline-none focus:ring-2 focus:ring-offset-2
              ${isSuccess ? "focus:ring-emerald-500" : "focus:ring-red-500"}
            `}
            onClick={handleManualClose}
            aria-label="Close notification"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const useToast = () => {
  const [ toast, setToast ] = useState<ToastProps | null>(null);

  const showToast = useCallback((message: string, type: ToastType) => {
    setToast({ message, type });
  }, []);

  const clearToast = useCallback(() => {
    setToast(null);
  }, []);

  const ToastComponent = () => (
    toast && (
      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    )
  );

  return {
    showToast,
    clearToast,
    Toast: ToastComponent
  };
};
